import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Lock, FileText, Download, Layers, List } from 'lucide-react';
import { Button } from '../components/Dashboard/ui/button';

function AdminFeedback() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [feedbackList, setFeedbackList] = useState([]);
    const [viewMode, setViewMode] = useState('individual'); // 'individual' or 'grouped'
    const navigate = useNavigate();

    useEffect(() => {
        const auth = getAuth();
        const unsub = onAuthStateChanged(auth, (u) => {
            setUser(u);
            if (!u) {
                navigate('/'); // Redirect to home if not logged in
            }
            setLoading(false);
        });
        return () => unsub();
    }, [navigate]);

    useEffect(() => {
        if (!user) return;

        // Removed orderBy('time') as we are no longer recording time
        const q = query(collection(db, 'final_feedback'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const feedbackData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setFeedbackList(feedbackData);
        });

        return () => unsubscribe();
    }, [user]);

    const exportToCSV = () => {
        if (feedbackList.length === 0) return;

        // Define headers - Removed User Email and Date
        const headers = ['Q1: Vision Reflection', 'Q2: Improvements', 'Q3: Missing Ideas', 'Q4: Most Important'];

        // Helper to sanitize fields for CSV (escape quotes and prevent formula injection)
        const sanitize = (text) => {
            if (!text) return '""';
            let safeText = text.toString().replace(/"/g, '""');
            // Prevent formula injection in Excel (leading =, +, -, @)
            if (/^[=+\-@]/.test(safeText)) {
                safeText = "'" + safeText;
            }
            return `"${safeText}"`;
        };

        // Map data to rows with proper quoting
        const rows = feedbackList.map(item => [
            sanitize(item.q1),
            sanitize(item.q2),
            sanitize(item.q3),
            sanitize(item.q4)
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `feedback_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Helper to get all answers for a specific question
    const getAnswersForQuestion = (key) => {
        return feedbackList
            .filter(item => item[key] && item[key].trim() !== '')
            .map(item => ({
                id: item.id,
                text: item[key]
            }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
            </div>
        );
    }

    if (!user) return null;

    return (
        <div className="min-h-screen bg-black text-white p-8 pt-24">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 border-b border-white/10 pb-6">
                    <div className="flex items-center gap-4">
                        <Lock className="h-8 w-8 text-secondary" />
                        <div>
                            <h1 className="text-3xl font-bold text-secondary">Admin Feedback Panel</h1>
                            <span className="text-sm text-gray-400">
                                {feedbackList.length} Total Submissions
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="bg-white/10 p-1 rounded-lg flex gap-1">
                            <button
                                onClick={() => setViewMode('individual')}
                                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${viewMode === 'individual' ? 'bg-secondary text-black' : 'text-gray-400 hover:text-white'}`}
                            >
                                <List className="h-4 w-4" />
                                Individual
                            </button>
                            <button
                                onClick={() => setViewMode('grouped')}
                                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${viewMode === 'grouped' ? 'bg-secondary text-black' : 'text-gray-400 hover:text-white'}`}
                            >
                                <Layers className="h-4 w-4" />
                                Grouped by Question
                            </button>
                        </div>

                        <Button
                            onClick={exportToCSV}
                            variant="outline"
                            className="gap-2 border-secondary/50 text-secondary hover:bg-secondary/10"
                        >
                            <Download className="h-4 w-4" />
                            Export CSV
                        </Button>
                    </div>
                </div>

                {feedbackList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 bg-white/5 rounded-lg border border-white/10">
                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No feedback submitted yet.</p>
                    </div>
                ) : (
                    <>
                        {viewMode === 'individual' ? (
                            <div className="grid gap-6">
                                {feedbackList.map((item) => (
                                    <div key={item.id} className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <div>
                                                    <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-1">1. Vision Reflection</h3>
                                                    <p className="text-gray-200 bg-black/20 p-3 rounded border border-white/5">{item.q1 || <span className="text-gray-600 italic">No answer</span>}</p>
                                                </div>
                                                <div>
                                                    <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-1">2. Suggested Improvements</h3>
                                                    <p className="text-gray-200 bg-black/20 p-3 rounded border border-white/5">{item.q2 || <span className="text-gray-600 italic">No answer</span>}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-4">
                                                <div>
                                                    <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-1">3. Missing Ideas</h3>
                                                    <p className="text-gray-200 bg-black/20 p-3 rounded border border-white/5">{item.q3 || <span className="text-gray-600 italic">No answer</span>}</p>
                                                </div>
                                                <div>
                                                    <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-1">4. Most Important</h3>
                                                    <p className="text-gray-200 bg-black/20 p-3 rounded border border-white/5">{item.q4 || <span className="text-gray-600 italic">No answer</span>}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-6">
                                {[
                                    { id: 'q1', title: '1. Vision Reflection' },
                                    { id: 'q2', title: '2. Suggested Improvements' },
                                    { id: 'q3', title: '3. Missing Ideas' },
                                    { id: 'q4', title: '4. Most Important' }
                                ].map((question) => {
                                    const answers = getAnswersForQuestion(question.id);
                                    return (
                                        <div key={question.id} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden flex flex-col h-[600px]">
                                            <div className="bg-white/5 p-4 border-b border-white/10">
                                                <h3 className="font-bold text-lg text-secondary">{question.title}</h3>
                                                <span className="text-xs text-gray-400">{answers.length} Responses</span>
                                            </div>
                                            <div className="p-4 overflow-y-auto space-y-3 flex-1 custom-scrollbar">
                                                {answers.length === 0 ? (
                                                    <p className="text-gray-500 text-center py-8 italic">No responses for this question.</p>
                                                ) : (
                                                    answers.map((ans) => (
                                                        <div key={ans.id} className="bg-black/40 p-3 rounded border border-white/5">
                                                            <p className="text-gray-200 text-sm mb-2">{ans.text}</p>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminFeedback;
